import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getMyPremiumStatus } from "@/lib/premium.functions";

export function usePremium(
  email: string | null | undefined,
  opts?: { enabled?: boolean },
) {
  const [until, setUntil] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const enabled = opts?.enabled ?? true;

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      setLoading(true);
      return () => {
        cancelled = true;
      };
    }

    if (!email) {
      setUntil(null);
      setLoading(false);
      return;
    }
    setLoading(true);

    // Primärt: server function (admin-klient) – förbi RLS, ingen
    // JWT-hydration-race. Fallback: direkt klient-query mot
    // premium_access (RLS-skyddad) ifall serverfn skulle fela.
    // Viktigt: ett fel får ALDRIG tolkas som "ingen premium" – då visas
    // betalvägg för betalande kunder. Vi gör istället korta omförsök och
    // håller status som "laddar" tills vi har ett säkert svar.
    (async () => {
      for (let attempt = 0; attempt < 4; attempt++) {
        if (cancelled) return;
        try {
          const res = await getMyPremiumStatus();
          if (cancelled) return;
          setUntil(res?.activeUntil ? new Date(res.activeUntil) : null);
          setLoading(false);
          return;
        } catch {
          // fallthrough till klient-query
        }
        const { data, error } = await supabase
          .from("premium_access")
          .select("active_until")
          .eq("email", email.toLowerCase())
          .gte("active_until", new Date().toISOString())
          .order("active_until", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        if (!error) {
          setUntil(data?.active_until ? new Date(data.active_until) : null);
          setLoading(false);
          return;
        }
        await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
      }
      if (cancelled) return;
      // Alla försök misslyckades – lämna status okänd men sluta ladda så att
      // pollningen i vyerna tar över.
      setLoading(false);
    })();


    return () => {
      cancelled = true;
    };
  }, [email, enabled, nonce]);

  return { premium: !!until && until.getTime() > Date.now(), until, loading, refresh };
}

// Säkerhetsnät för nyss betalda kunder: polla premium-status en begränsad tid
// med växande intervall. Tidigare pollades var 5:e sekund utan tak, vilket vid
// utgången inloggning gav oändliga 403 session_not_found mot auth-servern.
// Pollningen stoppas direkt om sessionen inte längre går att förnya, och då
// får vyn visa "logga in igen" istället för en tyst låst sida.
const POLL_DELAYS_MS = [5000, 5000, 5000, 10000, 10000, 20000, 30000, 60000];

export function usePremiumRecheck(opts: { enabled: boolean; refresh: () => void }) {
  const { enabled, refresh } = opts;
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    let attempt = 0;
    let timer: number | undefined;

    const schedule = () => {
      const delay = POLL_DELAYS_MS[Math.min(attempt, POLL_DELAYS_MS.length - 1)];
      timer = window.setTimeout(tick, delay);
    };

    const tick = async () => {
      if (stopped) return;
      // Kontrollera att sessionen fortfarande är giltig innan vi anropar
      // servern – annars blir varje poll ett auth-fel.
      let hasSession = false;
      try {
        const { data } = await supabase.auth.getSession();
        hasSession = !!data.session?.access_token;
      } catch {
        hasSession = false;
      }
      if (stopped) return;
      if (!hasSession) {
        setSessionExpired(true);
        return; // ingen ny poll – loopen avslutas
      }
      refresh();
      attempt += 1;
      if (attempt >= POLL_DELAYS_MS.length) return; // tak nått, sluta polla
      schedule();
    };

    schedule();
    return () => {
      stopped = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [enabled, refresh]);

  return { sessionExpired };
}

export function useIsAdmin(userId: string | null | undefined) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setIsAdmin(!!data);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId, nonce]);

  return { isAdmin, loading, refresh: () => setNonce((n) => n + 1) };
}
